import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { API_CONFIG, getAuthHeader, buildUrl } from "../config/api";
import { ApiError } from "../types";

class ApiService {
    private client: AxiosInstance;
    private accessToken: string | null = null;
    private refreshToken: string | null = null;

    constructor() {
        this.client = axios.create({
            baseURL: API_CONFIG.BASE_URL,
            timeout: API_CONFIG.TIMEOUT,
            headers: API_CONFIG.HEADERS,
        });

        this.setupInterceptors();
        this.loadTokensFromStorage();
    }

    private setupInterceptors() {
        // Request interceptor to add auth token
        this.client.interceptors.request.use(
            (config) => {
                if (this.accessToken) {
                    Object.assign(
                        config.headers,
                        getAuthHeader(this.accessToken)
                    );
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor to handle token refresh
        this.client.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        await this.refreshAccessToken();
                        return this.client(originalRequest);
                    } catch (refreshError) {
                        this.clearTokens();
                        window.location.href = "/login";
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    private loadTokensFromStorage() {
        this.accessToken = localStorage.getItem("access_token");
        this.refreshToken = localStorage.getItem("refresh_token");
    }

    private saveTokensToStorage(accessToken: string, refreshToken: string) {
        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", refreshToken);
        this.accessToken = accessToken;
        this.refreshToken = refreshToken;
    }

    private clearTokens() {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        this.accessToken = null;
        this.refreshToken = null;
    }

    private async refreshAccessToken(): Promise<void> {
        if (!this.refreshToken) {
            throw new Error("No refresh token available");
        }

        const response = await axios.post(
            buildUrl(API_CONFIG.ENDPOINTS.AUTH.REFRESH),
            { refresh_token: this.refreshToken }
        );

        const { access_token, refresh_token } = response.data;
        this.saveTokensToStorage(access_token, refresh_token);
    }

    // Get current user info
    async getCurrentUser(): Promise<any> {
        return this.get("/users/me");
    }

    // Generic API methods
    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse<T> = await this.client.get(url, config);
        return response.data;
    }

    async post<T>(
        url: string,
        data?: any,
        config?: AxiosRequestConfig
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.client.post(
            url,
            data,
            config
        );
        return response.data;
    }

    async put<T>(
        url: string,
        data?: any,
        config?: AxiosRequestConfig
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.client.put(
            url,
            data,
            config
        );
        return response.data;
    }

    async patch<T>(
        url: string,
        data?: any,
        config?: AxiosRequestConfig
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.client.patch(
            url,
            data,
            config
        );
        return response.data;
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse<T> = await this.client.delete(
            url,
            config
        );
        return response.data;
    }

    // Authentication methods
    async login(username: string, password: string) {
        const formData = new FormData();
        formData.append("username", username);
        formData.append("password", password);

        const response = await axios.post(
            buildUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN),
            formData,
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            }
        );

        const { access_token, refresh_token } = response.data;
        this.saveTokensToStorage(access_token, refresh_token);
        return response.data;
    }

    async register(email: string, password: string) {
        const response = await axios.post(
            buildUrl(API_CONFIG.ENDPOINTS.AUTH.REGISTER),
            { email, password }
        );
        return response.data;
    }

    // No OTP — a super admin reviews and approves the request out-of-band.
    // Always resolves with a generic message; never reveals whether the
    // email matches an account.
    async forgotPassword(email: string): Promise<{ message: string }> {
        const response = await axios.post(
            buildUrl(API_CONFIG.ENDPOINTS.AUTH.FORGOT_PASSWORD),
            { email }
        );
        return response.data;
    }

    async resetPassword(
        token: string,
        newPassword: string
    ): Promise<{ message: string }> {
        const response = await axios.post(
            buildUrl(API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD),
            { token, new_password: newPassword }
        );
        return response.data;
    }

    logout() {
        this.clearTokens();
    }

    isAuthenticated(): boolean {
        return !!this.accessToken;
    }

    // Error handling helper
    handleApiError(error: any): ApiError {
        if (error.response?.data) {
            return error.response.data;
        }
        return {
            detail: error.message || "An unexpected error occurred",
        };
    }
}

export const apiService = new ApiService();
